/**
 * Utilitaire pour résoudre les conflits entre différentes extensions de wallet
 * et nettoyer les erreurs non critiques
 */

interface WalletConflictResolver {
  suppressMetamaskConflicts(): void;
  cleanupErrorLogs(): void;
  initializeWalletPriority(): void;
}

class WalletConflictResolverImpl implements WalletConflictResolver {
  private originalConsoleError = console.error;
  private originalConsoleWarn = console.warn;
  private permissionErrorShown = false;
  private walletConflictErrorShown = false;
  private permissionWarningShown = false;

  constructor() {
    this.initializeWalletPriority();
  }

  /**
   * Supprime les conflits spécifiques à Metamask/Solflare
   */
  suppressMetamaskConflicts(): void {
    if (typeof window === 'undefined') return;

    try {
      // Intercepter et filtrer les erreurs de conflit de wallet
      console.error = (...args) => {
        const message = args.join(' ');
        
        // Ignorer les erreurs spécifiques de conflit wallet
        const conflictPatterns = [
          'solflare-detect-metamask',
          'StreamMiddleware - Unknown response id',
          'inpage.js:1 StreamMiddleware',
          'overrideMethod @ hook.js',
          'contentscript.js',
          'postMessage',
          '_postMessage',
          '_onMessage',
          '_onData',
          '_write',
          'Missing or insufficient permissions'
        ];

        if (conflictPatterns.some(pattern => message.includes(pattern))) {
          // Réduire le bruit dans la console pour les erreurs répétitives
          if (message.includes('Missing or insufficient permissions')) {
            // N'afficher qu'une seule fois ce type d'erreur
            if (!this.permissionErrorShown) {
              this.permissionErrorShown = true;
              console.log('🚨 Erreur Firebase détectée (logs supprimés pour éviter le spam)');
            }
            return;
          }
          
          if (message.includes('solflare-detect-metamask') && !this.walletConflictErrorShown) {
            this.walletConflictErrorShown = true;
            console.log('🔧 Conflit wallet Solflare/MetaMask détecté et supprimé');
            return;
          }
          
          return; // Supprimer complètement les autres erreurs de conflit
        }

        // Laisser passer les autres erreurs
        this.originalConsoleError.apply(console, args);
      };

      console.warn = (...args) => {
        const message = args.join(' ');
        
        // Filtrer certains warnings non critiques
        const warningPatterns = [
          'wallet-adapter',
          'deprecated',
          'StreamMiddleware',
          'PERMISSION DENIED - Causes possibles'
        ];

        if (warningPatterns.some(pattern => message.includes(pattern))) {
          // Réduire le spam des warnings Firebase
          if (message.includes('PERMISSION DENIED') && !this.permissionWarningShown) {
            this.permissionWarningShown = true;
            console.log('🚨 Problème de permissions Firebase détecté - voir documentation');
            return;
          }
          return;
        }

        this.originalConsoleWarn.apply(console, args);
      };

    } catch (error) {
      console.log('Erreur lors de la suppression des conflits wallet:', error);
    }
  }

  /**
   * Nettoie les logs d'erreur en excès
   */
  cleanupErrorLogs(): void {
    // Limiter le nombre d'erreurs identiques consécutives
    let lastError = '';
    let errorCount = 0;
    const maxSameErrors = 3;

    const originalError = console.error;
    console.error = (...args) => {
      const message = args.join(' ');
      
      if (message === lastError) {
        errorCount++;
        if (errorCount > maxSameErrors) {
          return; // Ignorer après N erreurs identiques
        }
      } else {
        lastError = message;
        errorCount = 1;
      }

      originalError.apply(console, args);
    };
  }

  /**
   * Initialise la priorité des wallets pour éviter les conflits
   */
  initializeWalletPriority(): void {
    if (typeof window === 'undefined') return;

    try {
      // Donner la priorité aux wallets Solana par rapport à Ethereum
      const anyWindow = window as any;
      
      // Si Solflare et Metamask sont présents, configurer la priorité
      if (anyWindow.solflare && anyWindow.ethereum) {
        // Marquer Solflare comme wallet principal pour Solana
        anyWindow.solana = anyWindow.solflare;
        console.log('🚀 Priorité wallet: Solflare configuré comme wallet Solana principal');
      }

      // Configuration des handlers d'événements pour éviter les conflits
      if (anyWindow.addEventListener) {
        anyWindow.addEventListener('wallet-standard:register-wallet', (event: any) => {
          // Ignorer certains événements de conflit
          if (event.detail?.name?.includes('Metamask') && anyWindow.solflare) {
            event.stopPropagation();
            return false;
          }
        }, true);
      }

    } catch (error) {
      console.log('Info: Configuration priorité wallet ignorée:', error);
    }
  }

  /**
   * Restaure les fonctions console originales
   */
  restore(): void {
    console.error = this.originalConsoleError;
    console.warn = this.originalConsoleWarn;
  }
}

// Instance singleton
export const walletConflictResolver = new WalletConflictResolverImpl();

// Fonction d'initialisation à appeler au démarrage de l'app
export const initializeWalletConflictResolution = () => {
  walletConflictResolver.suppressMetamaskConflicts();
  walletConflictResolver.cleanupErrorLogs();
  
  console.log('🔧 Résolveur de conflits wallet initialisé');
};

// Auto-initialisation si dans un navigateur
if (typeof window !== 'undefined') {
  // Attendre que le DOM soit prêt
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWalletConflictResolution);
  } else {
    initializeWalletConflictResolution();
  }
}
